import type { PlayerWithPlugins } from 'video.js';

import config from 'js/app/config';

import {
  DEFAULT_LOW_BANDWIDTH_VIDEO_PLAYER_RESOLUTION,
  DEFAULT_NORMAL_BANDWIDTH_VIDEO_PLAYER_RESOLUTION,
  LOW_BANDWIDTH_COUNTRIES,
  VIDEO_PLAYER_AUDIO_LANGUAGE_NOT_SET,
} from 'bundles/video-player/constants';
import type { SourcesByResolution } from 'bundles/video-player/types/VideoContent';
import userPreferences from 'bundles/video-player/utils/userPreferences';

export const getDefaultVideoResolution = () => {
  if (LOW_BANDWIDTH_COUNTRIES.includes(config.requestCountryCode)) {
    return DEFAULT_LOW_BANDWIDTH_VIDEO_PLAYER_RESOLUTION;
  } else {
    return DEFAULT_NORMAL_BANDWIDTH_VIDEO_PLAYER_RESOLUTION;
  }
};

// This function checks to make sure the selected resolution the learner has whether that be
// from the getDefaultVideoResolution or saved in the learner's user preferences is available
// for the current video they are watching. If not, it'll select the highest available resolution
export const getVideoSource = (
  videoSources: SourcesByResolution,
  selectedResolution: string
): { [key: string]: string } | undefined => {
  const resolutions = Object.keys(videoSources ?? {});
  const sortedResolutions = resolutions.sort((a, b) => parseInt(b, 10) - parseInt(a, 10));

  if (videoSources[selectedResolution]) {
    return videoSources[selectedResolution];
  }

  return videoSources[sortedResolutions[0]];
};

// Format resolutions into the format expected by VideoJS's resolutions plugin.
export const formatSourcesByResolution = (
  sources: SourcesByResolution
): { [key: string]: { type: string; src: string }[] } => {
  const sourcesByResolution: { [key: string]: { type: string; src: string }[] } = {};

  Object.entries(sources).forEach(([resolution, resolutionSources]) => {
    sourcesByResolution[resolution] = Object.entries(resolutionSources).map(([type, src]) => ({
      type,
      src,
    }));
  });

  return sourcesByResolution;
};

// This function returns the default language key value for the audio dubbing feature's eventing.
// If the audio language is set to default, the event value should be 'none' to represent that the default
// language value is currently being used.
export const getEventingDubbingLanguage = (defaultLanguageKey: string, selectedLanguageKey?: string) => {
  if (!selectedLanguageKey) {
    return VIDEO_PLAYER_AUDIO_LANGUAGE_NOT_SET;
  }

  return defaultLanguageKey === selectedLanguageKey ? VIDEO_PLAYER_AUDIO_LANGUAGE_NOT_SET : selectedLanguageKey;
};

export const defaultOnLoadedMetadata = ({
  player,
  currentTime,
  playbackRate,
  isPaused,
}: {
  player: PlayerWithPlugins;
  currentTime: number | undefined;
  playbackRate: number | undefined;
  isPaused: boolean;
}) => {
  player.currentTime(currentTime);
  player.playbackRate(playbackRate);

  const autoplay = userPreferences.get('autoplay');
  // if the video was paused, keep it paused, otherwise play it
  if (isPaused && !autoplay) {
    player.pause();
  } else {
    player.play();
  }
};

export const loadNewSrc = ({
  player,
  type,
  src,
  onLoadedMetadata,
}: {
  src: string;
  /**  Ex: 'video/mp4' or 'application/x-mpegURL' */
  type: string;
  player: PlayerWithPlugins;
  onLoadedMetadata?: () => {};
}) => {
  const isPaused = player.paused();
  const currentTime = player.currentTime();
  const playbackRate = player.playbackRate();

  player.one('loadedmetadata', () => {
    if (onLoadedMetadata) {
      onLoadedMetadata();
    } else {
      defaultOnLoadedMetadata({ player, currentTime, playbackRate, isPaused });
    }
  });

  player.src({
    src,
    type,
  });
};
